import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ThemeToggle from "./ThemeToggle";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAuthenticated, logout } = useAuth();

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: "smooth" });
  };

  const navLinkClass =
    "text-sm transition-colors " +
    "text-foreground/80 hover:text-foreground " +
    "dark:text-muted-foreground dark:hover:text-foreground";
  const navButtonClass =
    "inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-lg transition-colors " +
    "border border-foreground/25 hover:border-foreground/40 hover:bg-foreground/5 " +
    "dark:border-border dark:hover:bg-secondary";

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-[#e4ddd0] bg-[#faf7f1]/90 backdrop-blur-xl dark:border-border dark:bg-background/95">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <span className="heading-display text-[1.65rem] leading-none text-foreground">
              Compost Developer
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo("features")} className={navLinkClass}>
              Features
            </button>
            <button onClick={() => scrollTo("compliance")} className={navLinkClass}>
              Compliance
            </button>
            <button onClick={() => scrollTo("pricing")} className={navLinkClass}>
              Pricing
            </button>
            <ThemeToggle />
            {isAuthenticated ? (
              <>
                <Link to="/app" className={navLinkClass}>
                  Dashboard
                </Link>
                <button onClick={() => { setMobileOpen(false); logout(); }} className={navButtonClass}>
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className={navLinkClass}>
                  Log in
                </Link>
                <Link
                  to="/book-demo"
                  className="inline-flex items-center justify-center px-5 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Book a Demo
                </Link>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            className={`md:hidden p-2 ${navLinkClass}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 space-y-3 border-t border-border dark:border-border">
            <button onClick={() => scrollTo("features")} className={`block w-full text-left px-3 py-2 ${navLinkClass}`}>
              Features
            </button>
            <button onClick={() => scrollTo("compliance")} className={`block w-full text-left px-3 py-2 ${navLinkClass}`}>
              Compliance
            </button>
            <button onClick={() => scrollTo("pricing")} className={`block w-full text-left px-3 py-2 ${navLinkClass}`}>
              Pricing
            </button>
            {isAuthenticated ? (
              <>
                <Link to="/app" className={`block px-3 py-2 ${navLinkClass}`} onClick={() => setMobileOpen(false)}>
                  Dashboard
                </Link>
                <button
                  onClick={() => { setMobileOpen(false); logout(); }}
                  className={`block w-full text-center px-5 py-2.5 rounded-lg ${navButtonClass}`}
                >
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className={`block px-3 py-2 ${navLinkClass}`} onClick={() => setMobileOpen(false)}>
                  Log in
                </Link>
                <Link
                  to="/book-demo"
                  className="block text-center px-5 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-medium rounded-lg"
                  onClick={() => setMobileOpen(false)}
                >
                  Book a Demo
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
